import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, GripVertical, Heading, ImageIcon, List, Plus, Quote, Trash2, Type } from "lucide-react";
import { Button, DropdownMenu, Input, Textarea } from "@/components/ui";
import { Blog, BlogRewriteArticleContext } from "@/services/bedrock";
import { cn } from "@/lib/utils";
import { BLOG_BLOCK_TYPES, emptyBlogBlock, moveItem, normalizeBlogSection } from "../blogBody";
import { BlogMediaBlock } from "./BlogMediaBlock";
import {
  BlogRewritePanel,
  BlogRewriteResult,
  BlogRewriteScope,
  RewriteHoverButton,
  RewriteHoverRegion,
} from "./BlogRewritePanel";
import { beginPointerReorder, ReorderGhost, ReorderGhostOverlay } from "./pointerReorder";

type RewriteTarget = "section" | "heading" | number;

type Props = {
  section: Blog.Section;
  sectionIndex: number;
  readOnly: boolean;
  blogId?: number;
  articleContext?: BlogRewriteArticleContext;
  defaultOpen?: boolean;
  dragging?: boolean;
  onChange: (section: Blog.Section) => void;
  onRemove: () => void;
  onHover?: (index: number | null, blockIndex?: number) => void;
  onExpand?: (index: number, blockIndex?: number) => void;
  onInputBlur?: () => void;
  onPreviewSection?: (section: Blog.Section | null) => void;
  onMoveStart?: (event: React.PointerEvent, index: number, card: HTMLElement) => void;
};

const BLOCK_STYLE: Record<
  Exclude<Blog.BlockType, "heading">,
  { label: string; icon: React.ElementType; chip: string; accent: string }
> = {
  body: {
    label: "Body",
    icon: Type,
    chip: "bg-sky-50 text-sky-700 border-sky-200",
    accent: "border-l-sky-400",
  },
  list: {
    label: "List",
    icon: List,
    chip: "bg-emerald-50 text-emerald-700 border-emerald-200",
    accent: "border-l-emerald-400",
  },
  pullQuote: {
    label: "Quote",
    icon: Quote,
    chip: "bg-amber-50 text-amber-800 border-amber-200",
    accent: "border-l-amber-400",
  },
  media: {
    label: "Media",
    icon: ImageIcon,
    chip: "bg-violet-50 text-violet-700 border-violet-200",
    accent: "border-l-violet-400",
  },
};

export const BlogSectionBlocks: React.FC<Props> = ({
  section,
  sectionIndex,
  readOnly,
  blogId,
  articleContext,
  defaultOpen = false,
  dragging,
  onChange,
  onRemove,
  onHover,
  onExpand,
  onInputBlur,
  onPreviewSection,
  onMoveStart,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [rewriteTarget, setRewriteTarget] = useState<RewriteTarget | null>(null);
  const [reviewingRewrite, setReviewingRewrite] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [blockDragIndex, setBlockDragIndex] = useState<number | null>(null);
  const [blockGhost, setBlockGhost] = useState<ReorderGhost | null>(null);
  const contentRef = useRef<Blog.Block[]>([]);
  const listRef = useRef<HTMLDivElement>(null);
  const blocks = normalizeBlogSection(section).blocks ?? [];
  const heading = blocks[0]?.type === "heading" ? blocks[0] : emptyBlogBlock("heading");
  const contentBlocks = blocks.slice(1);
  const headingText = heading.text ?? "";
  contentRef.current = contentBlocks;

  const commit = (nextContent: Blog.Block[], nextHeading = heading) => {
    onChange({ blocks: [nextHeading, ...nextContent] });
  };

  const updateHeading = (text: string) => {
    commit(contentBlocks, { type: "heading", text });
  };

  const updateBlock = (index: number, patch: Partial<Blog.Block>) => {
    commit(contentBlocks.map((block, i) => (i === index ? { ...block, ...patch } : block)));
  };

  const addBlock = (type: Exclude<Blog.BlockType, "heading">) => {
    commit([...contentBlocks, emptyBlogBlock(type)]);
  };

  const removeBlock = (index: number) => {
    commit(contentBlocks.filter((_, i) => i !== index));
  };

  const previewBlockIndex = (target: RewriteTarget): number | undefined => {
    if (target === "section") return undefined;
    if (target === "heading") return 0;
    return target + 1;
  };

  const focusPreview = (blockIndex: number) => {
    onExpand?.(sectionIndex, blockIndex);
  };

  const blurPreview = (event: React.FocusEvent<HTMLElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node)) return;
    if (rewriteTarget != null) return;
    if (!dragging) onInputBlur?.();
  };

  const canRewrite = !readOnly && !!blogId;
  const normalizedSection = normalizeBlogSection(section);

  const toggleRewrite = (target: RewriteTarget) => {
    const next = rewriteTarget === target ? null : target;
    setOpen(true);
    setRewriteTarget(next);
    if (next == null) {
      onInputBlur?.();
      return;
    }
    onExpand?.(sectionIndex, previewBlockIndex(next));
  };

  const closeRewrite = () => {
    setReviewingRewrite(false);
    setRewriteTarget(null);
    onPreviewSection?.(null);
    onInputBlur?.();
  };

  const applyRewrite = (result: BlogRewriteResult) => {
    if (result.kind !== "section") return;
    onChange(normalizeBlogSection(result.section));
    if (rewriteTarget != null) {
      onExpand?.(sectionIndex, previewBlockIndex(rewriteTarget));
    }
    setReviewingRewrite(false);
    setRewriteTarget(null);
  };

  const handleReviewing = (proposed: string | string[] | Blog.Section | null) => {
    const section = proposed && typeof proposed === "object" && !Array.isArray(proposed) ? proposed : null;
    setReviewingRewrite(!!section);
    onPreviewSection?.(section);
  };

  const rewriteBlockType = typeof rewriteTarget === "number" ? contentBlocks[rewriteTarget]?.type : undefined;
  const rewriteScope: BlogRewriteScope | null =
    rewriteTarget === "section"
      ? "section"
      : rewriteTarget === "heading"
      ? "heading"
      : rewriteBlockType === "body" || rewriteBlockType === "list" || rewriteBlockType === "pullQuote"
      ? rewriteBlockType
      : null;
  const rewriteBlockIndex =
    rewriteTarget === "heading" ? 0 : typeof rewriteTarget === "number" ? rewriteTarget + 1 : undefined;

  const fieldsReadOnly = readOnly || reviewingRewrite;

  const startBlockDrag = (event: React.PointerEvent, index: number, card: HTMLElement) => {
    beginPointerReorder({
      event,
      card,
      index,
      itemSelector: "[data-blog-block]",
      scope: listRef.current,
      onReorder: (from, to) => {
        const next = moveItem(contentRef.current, from, to);
        contentRef.current = next;
        commit(next);
      },
      onActiveChange: setBlockDragIndex,
      onGhostChange: setBlockGhost,
    });
  };

  return (
    <div
      ref={cardRef}
      data-blog-section
      className={cn(
        "flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-150",
        dragging && "opacity-40 border-indigo-200"
      )}
      onMouseLeave={() => {
        if (!dragging) onHover?.(null);
      }}
    >
      <RewriteHoverRegion
        className="-mx-1 flex items-center justify-between gap-2 rounded-xl px-1 py-1 hover:bg-indigo-50/80"
        onMouseEnter={() => onHover?.(sectionIndex)}
      >
        <div
          role="button"
          tabIndex={-1}
          aria-label={`Drag section ${sectionIndex + 1}`}
          className={cn(
            "shrink-0 rounded-md p-1 text-slate-400 touch-none",
            readOnly
              ? "cursor-default opacity-40"
              : "cursor-grab active:cursor-grabbing hover:bg-white hover:text-slate-600"
          )}
          onPointerDown={event => {
            if (readOnly || !cardRef.current) return;
            onMoveStart?.(event, sectionIndex, cardRef.current);
          }}
        >
          <GripVertical className="size-4" />
        </div>
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={open}
          aria-label={`${open ? "Collapse" : "Expand"} section ${sectionIndex + 1}`}
          onClick={() =>
            setOpen(current => {
              if (!current) onExpand?.(sectionIndex);
              else setRewriteTarget(null);
              return !current;
            })
          }
        >
          <ChevronDown className={cn("size-4 shrink-0 text-slate-500 transition-transform", !open && "-rotate-90")} />
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">
            {sectionIndex + 1}
          </span>
          <span className="min-w-0 truncate text-sm font-semibold text-slate-800">{headingText || "New section"}</span>
          {!open && (
            <span className="shrink-0 text-xs font-medium text-slate-400">
              {contentBlocks.length} {contentBlocks.length === 1 ? "component" : "components"}
            </span>
          )}
        </button>
        {canRewrite && (
          <RewriteHoverButton
            label="Rewrite section with AI"
            active={rewriteTarget === "section"}
            forceVisible={open}
            onClick={() => toggleRewrite("section")}
          />
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Remove section ${sectionIndex + 1}`}
          disabled={readOnly}
          onClick={onRemove}
        >
          <Trash2 className="size-4" />
        </Button>
      </RewriteHoverRegion>

      {open && (
        <RewriteHoverRegion
          className="flex flex-col gap-2 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3"
          onMouseEnter={() => onHover?.(sectionIndex, 0)}
          onFocus={() => focusPreview(0)}
          onBlur={blurPreview}
        >
          <div className="flex items-center gap-1">
            <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-indigo-700">
              <Heading className="size-3" />
              Heading
              <span className="font-medium normal-case tracking-normal text-indigo-500">optional</span>
            </span>
            <div className="flex-grow" />
            {canRewrite && (
              <RewriteHoverButton
                label="Rewrite heading with AI"
                active={rewriteTarget === "heading"}
                onClick={() => toggleRewrite("heading")}
              />
            )}
          </div>
          <Input
            id={`blog-section-${sectionIndex}-heading`}
            value={headingText}
            disabled={fieldsReadOnly}
            placeholder="Section heading"
            className="bg-white text-base font-semibold"
            onKeyDown={stopSpacePropagation}
            onChange={event => updateHeading(event.target.value)}
          />
        </RewriteHoverRegion>
      )}

      {open && (
        <div ref={listRef} className="flex flex-col gap-3">
          {contentBlocks.map((block, index) => {
            const inputId = `blog-section-${sectionIndex}-block-${index}`;
            const style = BLOCK_STYLE[block.type as Exclude<Blog.BlockType, "heading">];
            if (!style) return null;
            const Icon = style.icon;
            return (
              <RewriteHoverRegion
                key={`${block.type}-${index}`}
                data-blog-block
                className={cn(
                  "flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3 border-l-4",
                  style.accent,
                  blockDragIndex === index && "opacity-40"
                )}
                onMouseEnter={() => onHover?.(sectionIndex, index + 1)}
                onFocus={() => focusPreview(index + 1)}
                onBlur={blurPreview}
              >
                <div className="flex items-center gap-1">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide",
                      style.chip
                    )}
                  >
                    <Icon className="size-3" />
                    {style.label}
                  </span>
                  <div className="flex-grow" />
                  {canRewrite && block.type !== "media" && (
                    <RewriteHoverButton
                      label={`Rewrite ${style.label.toLowerCase()} with AI`}
                      active={rewriteTarget === index}
                      onClick={() => toggleRewrite(index)}
                    />
                  )}
                  <div
                    role="button"
                    tabIndex={-1}
                    aria-label={`Drag ${style.label}`}
                    className={cn(
                      "rounded-md p-1 text-slate-400 touch-none",
                      readOnly
                        ? "cursor-default opacity-40"
                        : "cursor-grab active:cursor-grabbing hover:bg-white hover:text-slate-600"
                    )}
                    onPointerDown={event => {
                      if (readOnly) return;
                      startBlockDrag(event, index, event.currentTarget.closest("[data-blog-block]") as HTMLElement);
                    }}
                  >
                    <GripVertical className="size-4" />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    aria-label={`Remove ${style.label}`}
                    disabled={readOnly}
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => removeBlock(index)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>

                {block.type === "body" && (
                  <BlockTextarea
                    id={inputId}
                    rows={5}
                    value={(block.paragraphs ?? []).join("\n\n")}
                    disabled={fieldsReadOnly}
                    placeholder="Write the paragraph…"
                    onValueChange={value => updateBlock(index, { paragraphs: value.split(/\n\s*\n/) })}
                  />
                )}

                {block.type === "list" && (
                  <BlockTextarea
                    id={inputId}
                    rows={3}
                    value={(block.items ?? []).join("\n")}
                    disabled={fieldsReadOnly}
                    placeholder="One item per line"
                    onValueChange={value => updateBlock(index, { items: value.split("\n") })}
                  />
                )}

                {block.type === "pullQuote" && (
                  <Input
                    id={inputId}
                    value={block.text ?? ""}
                    disabled={fieldsReadOnly}
                    placeholder="A memorable line"
                    className="bg-white italic"
                    onKeyDown={stopSpacePropagation}
                    onChange={event => updateBlock(index, { text: event.target.value })}
                  />
                )}

                {block.type === "media" && (
                  <BlogMediaBlock
                    block={block}
                    readOnly={readOnly}
                    inputId={inputId}
                    onChange={patch => updateBlock(index, patch)}
                  />
                )}
              </RewriteHoverRegion>
            );
          })}
        </div>
      )}

      {open && (
        <DropdownMenu>
          <DropdownMenu.Trigger
            render={
              <Button type="button" variant="outline" size="sm" disabled={readOnly} className="self-start">
                <Plus className="size-4" />
                Add component
              </Button>
            }
          />
          <DropdownMenu.Content>
            {BLOG_BLOCK_TYPES.map(option => (
              <DropdownMenu.Item key={option.type} onClick={() => addBlock(option.type)}>
                {option.label}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu>
      )}
      <ReorderGhostOverlay ghost={blockGhost} />
      {canRewrite && blogId && rewriteScope && (
        <BlogRewritePanel
          open
          blogId={blogId}
          scope={rewriteScope}
          previousSection={normalizedSection}
          blockIndex={rewriteBlockIndex}
          articleContext={articleContext}
          onRewritten={applyRewrite}
          onReviewing={handleReviewing}
          onDismiss={closeRewrite}
        />
      )}
    </div>
  );
};

const stopSpacePropagation = (event: React.KeyboardEvent) => {
  if (event.key === " " || event.code === "Space") event.stopPropagation();
};

const BlockTextarea: React.FC<{
  id: string;
  value: string;
  disabled?: boolean;
  rows?: number;
  placeholder?: string;
  onValueChange: (value: string) => void;
}> = ({ id, value, disabled, rows, placeholder, onValueChange }) => {
  const [text, setText] = useState(value);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setText(value);
  }, [value]);

  return (
    <Textarea
      id={id}
      rows={rows}
      value={text}
      disabled={disabled}
      placeholder={placeholder}
      className="bg-white"
      onFocus={() => {
        focusedRef.current = true;
      }}
      onBlur={() => {
        focusedRef.current = false;
      }}
      onKeyDown={stopSpacePropagation}
      onChange={event => {
        const next = event.target.value;
        setText(next);
        onValueChange(next);
      }}
    />
  );
};
